<template>

  <!-- Table Container Card -->
  <b-card
    no-body
  >
    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <v-select
            v-model="perPage"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector d-inline-block ml-50 mr-1"
          />
          <b-button
            v-if="canCreateSW(applyDocument)"
            v-b-toggle.sidebar-create-scientific-work
            variant="primary"
          >
            {{ $t('Create') }}
          </b-button>

        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-end">
            <b-form-input
              v-model="searchQuery"
              class="d-inline-block mr-1"
              :placeholder="$t('Search')"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="scientificWorkTable"
      :items="list"
      responsive
      :fields="tableColumns"
      primary-key="id"
      :sort-by.sync="sortBy"
      show-empty
      :empty-text="$t('No matching records found')"
      :sort-desc.sync="isSortDirDesc"
      :busy="isBusy"
      class="position-relative"
    >
      <template #head()="data">
        {{ $t(data.label) }}
      </template>

      <template #cell(index)="data">
        {{ data.index + 1 }}
      </template>

      <template #cell(scientific_work_type)="data">
        {{ $langName(getScientificWorkType(data.value)) }}
      </template>

      <template #cell(scientific_work)="data">
        <b-link
          v-if="data.value"
          :href="VUE_APP_BACKEND_BASE_URL + data.value"
          download="file"
          target="_blank"
        >
          <feather-icon
            icon="DownloadIcon"
            class="cursor-pointer"
            size="16"
          />
        </b-link>
      </template>

      <template #cell(scientific_work_url)="data">
        <b-link
          v-if="data.value"
          :href="data.value"
          target="_blank"
        >
          {{ $t('Link') }}
        </b-link>
      </template>

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            variant="primary"
            label=""
          />
        </div>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <feather-icon
            v-if="canUpdate(applyDocument)"
            v-b-toggle.sidebar-create-scientific-work
            icon="EditIcon"
            class="cursor-pointer mr-1"
            size="16"
            @click="update(data.item)"
          />
          <feather-icon
            v-if="canUpdate(applyDocument)"
            icon="TrashIcon"
            class="cursor-pointer mr-1"
            size="16"
            @click="remove(data.item)"
          />
        </div>
      </template>

    </b-table>
    <div class="mx-2 mb-2">
      <b-row>
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted">{{ $t("table summary", { from: dataMeta.from, to: dataMeta.to, of: dataMeta.of }) }} </span>
        </b-col>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >

          <b-pagination
            v-model="currentPage"
            :total-rows="total"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            prev-class="prev-item"
            next-class="next-item"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
            </template>
            <template #next-text>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>

        </b-col>
      </b-row>
      <create
        v-if="canCreateSW(applyDocument)"
        @refetchData="refetchData"
      />
    </div>
  </b-card>

</template>

<script>
import {
  BButton, BCard, BCol, BFormInput, BPagination,
  BRow, BSpinner, BTable, VBToggle, BLink,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { onUnmounted } from '@vue/composition-api'
import Ripple from 'vue-ripple-directive'
import store from '@/store'
import config from './config'
import scientificWorkModule from '@/store/scientific-work/index'
import Create from './Create.vue'
import permission from '../permission'

export default {
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BPagination,
    vSelect,
    Create,
    BSpinner,
    BLink,
  },
  computed: {
    applyDocument() {
      return this.$store.state.applyDocument.applyDocument
    },
  },
  created() {
    this.$store.commit(`${this.MODULE_NAME}/FRESH`)
    this.extraParams = { id: this.applyDocument.id }
  },
  methods: {
    getScientificWorkType(id) {
      return this.$store.getters['staticModule/getScientificWorkType'](id)
    },
    update(data) {
      this.$store.commit(`${this.MODULE_NAME}/UPDATE`, data)
    },
    remove(data) {
      this.$removeSwal(this, data, `${this.MODULE_NAME}/del`, this.refetchData)
    },
  },
  setup() {
    const { VUE_APP_BACKEND_BASE_URL } = process.env
    const MODULE_NAME = 'scientificWork'
    // Register module
    if (!store.hasModule(MODULE_NAME)) store.registerModule(MODULE_NAME, scientificWorkModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(MODULE_NAME)) store.unregisterModule(MODULE_NAME)
    })

    return {
      ...config(),
      MODULE_NAME,
      ...permission(),
      VUE_APP_BACKEND_BASE_URL,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
</style>
